import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon, ArrowLeft, Clock } from "lucide-react";
import { format, startOfDay, endOfDay } from "date-fns";
import { de } from "date-fns/locale";
import { useLanguage } from "@/hooks/useLanguage";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface FollowUp {
  id: string;
  time: string;
  name?: string;
  address: string;
  notes?: string;
}

interface CustomerAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (appointment: {
    title: string;
    date: Date;
    time: string;
    notes?: string;
  }) => void;
  customerName: string;
}

export const CustomerAppointmentDialog = ({
  open,
  onOpenChange,
  onConfirm,
  customerName,
}: CustomerAppointmentDialogProps) => {
  const { getLocale, isHoliday } = useLanguage();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<'calendar' | 'day'>('calendar');
  const [title, setTitle] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [dayViewDate, setDayViewDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState("09:00");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [existingFollowUps, setExistingFollowUps] = useState<FollowUp[]>([]);
  const [loadingFollowUps, setLoadingFollowUps] = useState(false);
  const [daysWithAppointments, setDaysWithAppointments] = useState<Set<string>>(new Set());

  // Load days with appointments for the current month
  const loadDaysWithAppointments = async (month: Date) => {
    if (!user) return;
    
    const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59);
    
    try {
      const { data } = await supabase
        .from('d2d_units')
        .select('next_reminder')
        .eq('user_id', user.id)
        .eq('manual_reminder', true)
        .gte('next_reminder', monthStart.toISOString())
        .lte('next_reminder', monthEnd.toISOString())
        .not('next_reminder', 'is', null);

      if (data) {
        const days = new Set(
          data.map(item => format(new Date(item.next_reminder), 'yyyy-MM-dd'))
        );
        setDaysWithAppointments(days);
      }
    } catch (error) {
      console.error('Error loading days with appointments:', error);
    }
  };

  // Load days with appointments when dialog opens
  useEffect(() => {
    if (open && user) {
      loadDaysWithAppointments(new Date());
    }
  }, [open, user]);

  const loadFollowUpsForDate = async (date: Date) => {
    if (!user) return;
    
    setLoadingFollowUps(true);
    try {
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);
      
      const { data: d2dUnitsData } = await supabase
        .from('d2d_units')
        .select(`
          id, 
          unit_name, 
          next_reminder, 
          follow_up_notes,
          d2d_houses!inner(
            house_number,
            d2d_streets!inner(
              name,
              plz
            )
          )
        `)
        .eq('user_id', user.id)
        .eq('manual_reminder', true)
        .gte('next_reminder', dayStart.toISOString())
        .lte('next_reminder', dayEnd.toISOString())
        .not('next_reminder', 'is', null);

      const followUps: FollowUp[] = [];

      if (d2dUnitsData) {
        d2dUnitsData.forEach(unit => {
          const time = format(new Date(unit.next_reminder), 'HH:mm', { locale: getLocale() });
          const address = `${unit.d2d_houses.d2d_streets.name} ${unit.d2d_houses.house_number}`;
          const name = unit.unit_name || `${unit.d2d_houses.d2d_streets.name} ${unit.d2d_houses.house_number}`;
          followUps.push({
            id: `d2d_${unit.id}`,
            time,
            name,
            address,
            notes: unit.follow_up_notes
          });
        });
      }

      followUps.sort((a, b) => a.time.localeCompare(b.time));
      setExistingFollowUps(followUps);
    } catch (error) {
      console.error('Error loading follow-ups:', error);
    } finally {
      setLoadingFollowUps(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setDayViewDate(date);
      setSelectedDate(date);
      setViewMode('day');
      loadFollowUpsForDate(date);
    }
  };

  const handleBackToCalendar = () => {
    setViewMode('calendar');
    setDayViewDate(undefined);
  };

  // Generate time slots from 8:00 to 20:00 in 15-minute intervals
  const generateTimeSlots = () => {
    const slots: string[] = [];
    for (let hour = 8; hour <= 20; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === 20 && minute > 0) break;
        const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeSlot);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const isTimeSlotTaken = (timeSlot: string) => {
    return existingFollowUps.some(followUp => followUp.time === timeSlot);
  };

  const handleConfirm = async () => {
    if (!selectedDate || !title.trim()) return;

    setCreating(true);
    try {
      const [hours, minutes] = selectedTime.split(':');
      const appointmentDate = new Date(selectedDate);
      appointmentDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      await onConfirm({
        title: title.trim(),
        date: appointmentDate,
        time: selectedTime,
        notes: notes.trim() || undefined,
      });

      // Reset form
      setTitle("");
      setSelectedDate(undefined);
      setSelectedTime("09:00");
      setNotes("");
      onOpenChange(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-[420px] sm:max-w-2xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-3 sm:p-6">
        <DialogHeader className="pb-2 sm:pb-4">
          <DialogTitle className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-lg">
            <CalendarIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            <span className="line-clamp-1 text-sm sm:text-base">{customerName}</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-2.5 sm:space-y-4">
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="title" className="text-xs sm:text-sm">Titel *</Label>
            <Input
              id="title"
              placeholder="z.B. Beratung..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
              className="text-xs sm:text-sm h-8 sm:h-10"
            />
          </div>

          {viewMode === 'calendar' ? (
            <div className="space-y-2 sm:space-y-3">
              <Label className="text-xs sm:text-sm font-medium">Datum *</Label>
              <div className="bg-card rounded-lg p-1.5 sm:p-4 border flex justify-center">
                <Calendar 
                  mode="single" 
                  selected={selectedDate} 
                  onSelect={handleDateSelect} 
                  disabled={(date) => date < startOfDay(new Date()) || isHoliday(date)}
                  initialFocus 
                  className="pointer-events-auto"
                  classNames={{
                    months: "flex justify-center",
                    month: "space-y-1.5 sm:space-y-4",
                    table: "w-auto border-collapse mx-auto",
                    head_row: "flex gap-0.5",
                    head_cell: "w-7 sm:w-9 text-center font-medium text-[10px] sm:text-sm",
                    row: "flex gap-0.5 mt-0.5 sm:mt-2",
                    cell: "w-7 sm:w-9 h-7 sm:h-9 text-center p-0",
                    day: "w-7 sm:w-9 h-7 sm:h-9 text-[10px] sm:text-sm font-medium hover:bg-accent hover:text-accent-foreground",
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                    day_today: "bg-accent text-accent-foreground",
                    day_outside: "text-muted-foreground opacity-50",
                    day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
                    day_hidden: "invisible"
                  }}
                  modifiers={{
                    hasAppointment: (date) => daysWithAppointments.has(format(date, 'yyyy-MM-dd'))
                  }}
                  modifiersClassNames={{
                    hasAppointment: "bg-orange-500/20 text-orange-900 dark:text-orange-100 font-bold hover:bg-orange-500/30"
                  }}
                  showOutsideDays={false}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleBackToCalendar}
                  className="gap-1 text-[10px] sm:text-sm h-7 sm:h-9 px-1.5 sm:px-3"
                >
                  <ArrowLeft className="h-2.5 w-2.5 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Zurück</span>
                </Button>
                <div className="flex-1 text-center">
                  <span className="text-xs sm:text-base font-semibold">
                    {dayViewDate && format(dayViewDate, 'dd. MMM', { locale: getLocale() })}
                  </span>
                </div>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <Label className="text-xs sm:text-sm font-medium">Uhrzeit *</Label>
                <div className="grid grid-cols-4 gap-1 sm:gap-2 max-h-[250px] sm:max-h-[400px] overflow-y-auto p-1.5 sm:p-2 border rounded-lg">
                  {timeSlots.map((timeSlot) => {
                    const isTaken = isTimeSlotTaken(timeSlot);
                    
                    return (
                      <Button
                        key={timeSlot}
                        variant={selectedTime === timeSlot ? 'default' : 'outline'}
                        className={cn(
                          "h-8 sm:h-12 font-medium transition-all relative p-0.5 sm:p-2",
                          isTaken && selectedTime !== timeSlot && "opacity-50 cursor-not-allowed"
                        )}
                        onClick={() => !isTaken && setSelectedTime(timeSlot)}
                        disabled={isTaken && selectedTime !== timeSlot}
                      >
                        <span className="text-[9px] sm:text-xs leading-tight">{timeSlot}</span>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {existingFollowUps.length > 0 && (
                <div className="border rounded-md p-1.5 sm:p-3 bg-muted/20">
                  <h4 className="font-semibold text-[10px] sm:text-sm mb-1.5 sm:mb-2 flex items-center gap-1 sm:gap-2">
                    <CalendarIcon className="h-2.5 w-2.5 sm:h-4 sm:w-4" />
                    <span className="truncate">Termine ({existingFollowUps.length})</span>
                  </h4>
                  <div className="space-y-1 sm:space-y-2 max-h-[120px] sm:max-h-[200px] overflow-y-auto">
                    {existingFollowUps.map((followUp) => (
                      <div 
                        key={followUp.id} 
                        className="flex items-start gap-1 sm:gap-2 p-1 sm:p-2 bg-background rounded border"
                      >
                        <div className="flex items-center gap-0.5 sm:gap-1.5 min-w-[38px] sm:min-w-[60px]">
                          <Clock className="h-2 w-2 sm:h-3 sm:w-3 text-muted-foreground flex-shrink-0" />
                          <span className="font-medium text-[9px] sm:text-xs">{followUp.time}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-[10px] sm:text-sm">{followUp.name}</div>
                          <div className="text-[9px] sm:text-xs text-muted-foreground truncate">{followUp.address}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="notes" className="text-xs sm:text-sm">Notizen</Label>
            <Textarea
              id="notes"
              placeholder="Optional..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="text-xs sm:text-sm resize-none"
            />
          </div>

          {selectedDate && viewMode === 'day' && (
            <div className="p-1.5 sm:p-3 bg-primary/10 rounded-md">
              <div className="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-sm">
                <CalendarIcon className="h-2.5 w-2.5 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="font-medium truncate">
                  {format(selectedDate, 'dd. MMM', { locale: de })} • {selectedTime}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-1.5 sm:gap-2 justify-end pt-1.5 sm:pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
            className="text-xs sm:text-sm h-8 sm:h-10"
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedDate || !title.trim() || creating || viewMode === 'calendar'}
            className="text-xs sm:text-sm h-8 sm:h-10"
          >
            {creating ? 'Erstelle...' : 'Erstellen'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
